import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ProductCard } from "@/components/ProductCard";
import { getCategoryBySlug } from "@/lib/categories";
import { cleanProductTitle, uniqueDisplayNames } from "@/lib/productTitle";
import { fetchProductByHandle, type ShopifyProduct } from "@/lib/shopify";
import { defaultVariant } from "@/lib/variants";
import { ArticleProductBlock } from "@/components/journal/ArticleProductBlock";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Stands in for one ProductCard while the picks load: the same 4:3 photo box,
 * two-line name, price line and (on phones) button row. Rendering nothing
 * instead let the rail push everything below it down a full screen when it
 * arrived: CLS 0.154 on /get-the-look.
 */
const PickPlaceholder = () => (
  <div aria-hidden="true">
    <Skeleton className="aspect-[4/3] w-full rounded-none" />
    <div className="flex flex-col items-center pt-3 pb-1">
      <Skeleton className="h-[37px] w-4/5" />
      <Skeleton className="mt-1 h-[19.5px] w-1/3" />
      <Skeleton className="mt-3 h-9 w-full rounded-full md:hidden" />
    </div>
  </div>
);

// The measure a supplier title opens with: 42", 8', 20" X 20".
const LEADING_SIZE = /^\s*(\d+(?:\.\d+)?\s?(?:"|'|in\.?|ft\.?)(?:\s*[xX×]\s*\d+(?:\.\d+)?\s?(?:"|'|in\.?|ft\.?)?)?)(?=\s)/;

/**
 * A guide's picks are chosen by size: keep the measure the title leads with,
 * as the guide's own list and the product page do. Two wall clocks of 24" and
 * 18" were both "Gold Iron Wall Clock".
 */
function withLeadingSize(title: string, name: string): string {
  const size = title.match(LEADING_SIZE)?.[1].replace(/\s*[xX×]\s*/, " x ");
  if (!size) return name;
  const first = size.match(/^\d+(?:\.\d+)?/)![0];
  return new RegExp(`(^|\\D)${first.replace(".", "\\.")}\\s?["']`).test(name) ? name : `${size} ${name}`;
}

interface ArticlePicksBlockProps {
  handles: string[];
  categoryPath?: string;
  onResolvedChange?: (rendered: boolean) => void;
  heading?: string;
}

export const ArticlePicksBlock = ({
  handles,
  categoryPath,
  onResolvedChange = () => {},
  heading = "Shop this guide",
}: ArticlePicksBlockProps) => {
  const [products, setProducts] = useState<ShopifyProduct[] | null>(null);
  const signature = handles.join("|");

  useEffect(() => {
    let cancelled = false;
    setProducts(null);
    onResolvedChange(false);

    void Promise.all(
      handles.map(async (handle) => {
        try {
          const node = await fetchProductByHandle(handle);
          return node ? ({ node } satisfies ShopifyProduct) : null;
        } catch {
          return null;
        }
      }),
    ).then((resolved) => {
      if (cancelled) return;
      // Sold-out picks are skipped: a "SOLD OUT" card took a third of a 3-card rail.
      // So is one whose titled size is the sold-out one: the pick is that size.
      const available = resolved.filter(
        (product): product is ShopifyProduct =>
          product !== null &&
          product.node.availableForSale !== false &&
          defaultVariant(product.node)?.availableForSale !== false,
      );
      setProducts(available);
      onResolvedChange(available.length > 0);
    });

    return () => {
      cancelled = true;
    };
    // The signature captures ordered handle changes without reloading for a new array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Keyed by handle. Each name keeps its size; if that makes two alike, the
  // shared uniqueness rule names them instead.
  const names = useMemo(() => {
    const list = products ?? [];
    const unique = uniqueDisplayNames(list);
    const sized = list.map((p) => withLeadingSize(p.node.title, cleanProductTitle(p.node.title, p.node.productType)));
    const distinct = new Set(sized.map((n) => n.toLowerCase())).size === sized.length;
    return Object.fromEntries(
      list.map((p, i) => [p.node.handle, distinct ? sized[i] : withLeadingSize(p.node.title, unique[p.node.handle])]),
    );
  }, [products]);
  const slug = categoryPath?.replace(/^\/category\//, "").split(/[?#]/)[0];
  const category = slug ? getCategoryBySlug(slug) : undefined;

  if (products !== null && products.length === 0) {
    return categoryPath ? <ArticleProductBlock categoryPath={categoryPath} /> : null;
  }

  return (
    <section className="not-prose my-12 border-t border-border/60 pt-8" aria-busy={products === null}>
      <div className="flex items-baseline justify-between gap-3 mb-5">
        <h2 className="text-[11px] md:text-[12px] tracking-[0.22em] uppercase font-bold text-foreground">
          {heading}
        </h2>
        {category && slug && (
          <Link
            to={`/category/${slug}`}
            className="text-[10px] tracking-[0.18em] uppercase font-bold text-foreground hover:text-primary inline-flex items-center gap-1.5 shrink-0"
          >
            {category.name} <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
        {products === null
          ? handles.map((handle, index) => <PickPlaceholder key={`${handle}-${index}`} />)
          : products.map((product, index) => (
              <ProductCard
                key={product.node.id}
                product={product}
                displayName={names[product.node.handle]}
                openingPrice
              />
            ))}
      </div>
    </section>
  );
};